import React, { useState, useEffect } from "react";
import GeneralTable from "../../components/Table/GeneralTable";
import "./TeamLoanApproval.scss";
import axiosInstance from "../../axiosInstance";
import IconButton from "../../components/IconButton/IconButton";
import { toast } from "react-toastify";

function TeamLoanApproval({ onRequestProcessed }) {
  const [loanReports, setLoanReports] = useState([]);
  const [loanRequests, setLoanRequests] = useState([]);
  const [showLoanReports, setShowLoanReports] = useState(false);

  // States for rejection popup
  const [showRejectPopup, setShowRejectPopup] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [selectedLoanId, setSelectedLoanId] = useState(null);

  useEffect(() => {
    fetchLoanRequests();
  }, []);

  const fetchLoanReports = async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_HR_URL}/emergency-fund/all`
      );

      setLoanReports(response.data.data);
    } catch (error) {
      console.error("Error fetching loan reports", error);
    }
  };

  const fetchLoanRequests = async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_HR_URL}/emergency-fund/requested`
      );
      setLoanRequests(response.data.data);
    } catch (error) {
      console.error("Error fetching loan requests", error);
    }
  };

  const handleRejectClick = (id) => {
    setSelectedLoanId(id);
    setShowRejectPopup(true);
    setRejectionReason(""); // Reset reason
  };

  const handleRejectConfirm = async () => {
    if (!rejectionReason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }

    try {
      await axiosInstance.put(
        `${process.env.REACT_APP_API_HR_URL}/emergency-fund/reject/${selectedLoanId}`,
        JSON.stringify(rejectionReason),
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      toast.success("Loan is rejected!");
      fetchLoanRequests();

      // Call the parent component's callback to update notification count
      if (onRequestProcessed) {
        onRequestProcessed?.();
      }

      // Reset states
      setShowRejectPopup(false);
      setSelectedLoanId(null);
      setRejectionReason("");
    } catch (error) {
      console.error("Error rejecting loan", error);
      toast.error("Failed to reject loan");
    }
  };

  const handleRejectCancel = () => {
    setShowRejectPopup(false);
    setSelectedLoanId(null);
    setRejectionReason("");
  };

  const handleApprove = async (id) => {
    try {
      await axiosInstance.put(
        `${process.env.REACT_APP_API_HR_URL}/emergency-fund/approve/${id}`
      );
      toast.success("Loan is approved!");
      fetchLoanRequests();

      // Call the parent component's callback to update notification count
      if (onRequestProcessed) {
        onRequestProcessed?.();
      }
    } catch (error) {
      console.error("Error approving loan", error);
    }
  };

  const loanColumns = [
    { header: "Employee", accessor: "employeeName" },
    { header: "Fund Type", accessor: "fundType" },
    { header: "Total Amount", accessor: "totalAmount" },
    { header: "Pending Amount", accessor: "pendingAmount" },
    { header: "Payment Status", accessor: "paymentStatus" },
    { header: "Approval Status", accessor: "fundApprovalStatus" },
  ];

  const requestColumns = [
    ...loanColumns,
    {
      header: "Actions",
      accessor: "actions",
    },
  ];

  const loanRequestsData = loanRequests.map((loan) => ({
    ...loan,
    actions: (
      <div className="action-buttons">
        <IconButton
          icon="mdi:tick"
          onClick={() => handleApprove(loan.emergencyFundId)}
          className="icon-button--save"
        />

        <IconButton
          icon="material-symbols-light:close"
          onClick={() => handleRejectClick(loan.emergencyFundId)}
          className="icon-button--delete"
        />
      </div>
    ),
  }));

  return (
    <div className="team-loan-approval">
      <div className="team-loan-container">
        {showLoanReports && (
          <GeneralTable
            columns={loanColumns}
            data={loanReports}
            className="loan-table"
            title="Loan report "
          />
        )}

        <GeneralTable
          columns={requestColumns}
          data={loanRequestsData}
          className="loan-table"
          title="Loan Requests"
          titleActions={
            <button
              className={`toggle-btn ${showLoanReports ? "active" : "inactive"}`}
              onClick={() => {
                setShowLoanReports(!showLoanReports);
                if (!showLoanReports) fetchLoanReports();
              }}
            >
              {showLoanReports ? "Close report" : "Loan reports"}
            </button>
          }
        />
      </div>

      {/* Rejection Reason Popup */}
      {showRejectPopup && (
        <div className="rejection-popup-overlay" onClick={handleRejectCancel}>
          <div className="rejection-popup" onClick={(e) => e.stopPropagation()}>
            <h2 className="rejection-popup-title">Reject Loan Request</h2>
            <p className="rejection-popup-message">
              Please provide a reason for rejecting this loan request:
            </p>
            <textarea
              className="rejection-reason-input"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              rows={4}
              autoFocus
            />
            <div className="rejection-popup-actions">
              <button
                className="rejection-popup-button cancel"
                onClick={handleRejectCancel}
              >
                Cancel
              </button>
              <button
                className="rejection-popup-button reject"
                onClick={handleRejectConfirm}
                disabled={!rejectionReason.trim()}
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamLoanApproval;
